import React, { useEffect, useState } from 'react'
import { STAGES, SLA, PERMS } from './crmData'
import { WEBHOOK_ENDPOINTS } from './crmData'
import { Card, crmToast, CrmToaster, SegTabs } from './crmUi'
import { Button } from '../shared/Button'
import { useCrm } from './useCrm'
import { useAgents } from './useAgents'
import { useAuth } from '../../contexts/AuthContext'
import { getSettings, putSettings } from './crmApi'
import { CrmCustomerSync } from './CrmCustomerSync'
import { CrmOwners } from './CrmOwners'

const ff = (p: Promise<unknown>) => {
  p.catch(() => {})
}

export const CrmSettings: React.FC = () => {
  const nonTerminal = STAGES.filter((s) => !s.terminal)
  const { agents } = useAgents()
  const { currentAgent } = useAuth()

  const { data: settings } = useCrm(getSettings, {
    stageWeights: Object.fromEntries(STAGES.map((s) => [s.key, s.weight])),
    slaDays: SLA,
    perms: Object.fromEntries(PERMS.map((p) => [p.key, p.enabled])),
    savedViews: [],
    savedReports: [],
  })

  const [probs, setProbs] = useState<Record<string, number>>(
    Object.fromEntries(nonTerminal.map((s) => [s.key, Math.round(s.weight * 100)]))
  )
  const [slas, setSlas] = useState<Record<string, number>>(
    Object.fromEntries(nonTerminal.map((s) => [s.key, SLA[s.key] || 0]))
  )
  const [perms, setPerms] = useState(PERMS)
  const [tab, setTab] = useState('ownership')

  // Sync editable local state from live settings once they load (falls back to mock).
  useEffect(() => {
    setProbs(
      Object.fromEntries(
        nonTerminal.map((s) => [s.key, Math.round((settings.stageWeights[s.key] ?? s.weight) * 100)])
      )
    )
    setSlas(
      Object.fromEntries(nonTerminal.map((s) => [s.key, settings.slaDays[s.key] ?? SLA[s.key] ?? 0]))
    )
    setPerms(PERMS.map((p) => ({ ...p, enabled: settings.perms[p.key] ?? p.enabled })))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings])

  const togglePerm = (key: string) => {
    setPerms((prev) => {
      const next = prev.map((p) =>
        p.key === key ? { ...p, enabled: !p.enabled } : p
      )
      const flipped = next.find((p) => p.key === key)
      if (flipped) crmToast(`${flipped.label} — ${flipped.enabled ? 'enabled' : 'disabled'}`)
      ff(putSettings({ perms: Object.fromEntries(next.map((p) => [p.key, p.enabled])) }))
      return next
    })
  }

  const saveProbabilities = () => {
    const stageWeights = Object.fromEntries(
      Object.entries(probs).map(([k, v]) => [k, v / 100])
    )
    ff(putSettings({ stageWeights }))
    crmToast('Stage probabilities saved — forecast updated', 'success')
  }

  const saveSla = () => {
    ff(putSettings({ slaDays: slas }))
    crmToast('SLA settings saved')
  }

  const SETTINGS_TABS = [
    { key: 'ownership', label: 'Lead ownership' },
    { key: 'sync', label: 'WeQuote data sync' },
    { key: 'pipeline', label: 'Pipeline & SLA' },
    { key: 'team', label: 'Team & roles' },
    { key: 'webhooks', label: 'Webhooks' },
  ]

  return (
    <div className="h-full flex flex-col bg-surface-secondary">
      <CrmToaster />

      {/* Header + tabs — pinned */}
      <div className="px-6 pt-6 pb-3 bg-surface border-b border-border shrink-0">
        <h1 className="text-xl font-bold text-txt-primary">Settings</h1>
        <p className="text-sm text-txt-secondary mb-3">Ownership, sync, pipeline, team and webhooks</p>
        <SegTabs tabs={SETTINGS_TABS} value={tab} onChange={setTab} />
      </div>

      {/* Content — only the active tab, and only it scrolls */}
      <div className="flex-1 min-h-0 overflow-auto p-6">
        <div className="max-w-[720px] space-y-4">
          {tab === 'ownership' && <CrmOwners />}

          {tab === 'sync' && <CrmCustomerSync />}

          {tab === 'pipeline' && (<>
          {/* 1) Stage forecast probabilities */}
          <Card
            title="Stage forecast probabilities"
            subtitle="Percentage of deal value counted toward weighted forecast at each stage"
          >
            <div className="space-y-2.5">
              {nonTerminal.map((s) => (
                <div key={s.key} className="flex items-center gap-3">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: s.color }} />
                  <span className="flex-1 text-sm text-txt-primary">{s.label}</span>
                  <div className="flex items-center gap-1.5">
                    <input
                      type="number"
                      value={probs[s.key]}
                      onChange={(e) => setProbs((p) => ({ ...p, [s.key]: Number(e.target.value) }))}
                      className="w-14 rounded-md border border-border bg-surface px-2 py-1 text-sm text-txt-primary text-right outline-none focus:border-brand-500"
                    />
                    <span className="text-sm text-txt-tertiary">%</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4">
              <Button size="sm" onClick={saveProbabilities}>
                Save probabilities
              </Button>
            </div>
          </Card>

          {/* 2) Stage SLA alerts */}
          <Card
            title="Stage SLA alerts"
            subtitle="Max days without activity before a warning/breach indicator appears on cards. Set 0 to disable."
          >
            <div className="space-y-2.5">
              {nonTerminal.map((s) => (
                <div key={s.key} className="flex items-center gap-3">
                  <span className="flex-1 text-sm text-txt-primary">{s.label}</span>
                  <div className="flex items-center gap-1.5">
                    <input
                      type="number"
                      value={slas[s.key]}
                      onChange={(e) => setSlas((p) => ({ ...p, [s.key]: Number(e.target.value) }))}
                      className="w-14 rounded-md border border-border bg-surface px-2 py-1 text-sm text-txt-primary text-right outline-none focus:border-brand-500"
                    />
                    <span className="text-sm text-txt-tertiary">days</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4">
              <Button size="sm" onClick={saveSla}>
                Save SLA
              </Button>
            </div>
          </Card>
          </>)}

          {tab === 'team' && (<>
          {/* 3) Team members */}
          <Card
            title="Team members"
            subtitle="Your CRM users are the support agents. Add or remove people in Admin → Agents — it applies everywhere, so it isn't duplicated here."
            actions={
              <Button size="sm" variant="secondary" onClick={() => crmToast('Manage agents in Admin → Agents — the same accounts power Support and the CRM', 'info')}>
                Manage agents →
              </Button>
            }
          >
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-txt-tertiary border-b border-border">
                  <th className="py-2 pr-3 font-medium">Name</th>
                  <th className="py-2 pr-3 font-medium">Email</th>
                  <th className="py-2 pr-3 font-medium">Role</th>
                  <th className="py-2 font-medium text-right" />
                </tr>
              </thead>
              <tbody>
                {agents.map((u) => (
                  <tr key={u.id} className="border-b border-border last:border-0">
                    <td className="py-2.5 pr-3 font-medium text-txt-primary">{u.name}</td>
                    <td className="py-2.5 pr-3 text-txt-secondary">{u.email}</td>
                    <td className="py-2.5 pr-3 text-txt-secondary">{(u.role || 'agent').replace(/_/g, ' ')}</td>
                    <td className="py-2.5 text-right">
                      {u.id === currentAgent?.id ? (
                        <span className="text-xs text-txt-tertiary">You</span>
                      ) : (
                        <span className="text-xs text-txt-tertiary">Managed in Admin → Agents</span>
                      )}
                    </td>
                  </tr>
                ))}
                {agents.length === 0 && (
                  <tr><td colSpan={4} className="py-6 text-center text-xs text-txt-tertiary">No agents found — add them in Admin → Agents.</td></tr>
                )}
              </tbody>
            </table>
          </Card>

          {/* 4) Role permissions */}
          <Card title="Role permissions" subtitle="Configure what sales reps can access.">
            <div className="space-y-2.5">
              {perms.map((p) => (
                <div key={p.key} className="flex items-center gap-3">
                  <span className="flex-1 text-sm text-txt-primary">{p.label}</span>
                  <button
                    onClick={() => togglePerm(p.key)}
                    className={`px-3 py-1 rounded-md text-xs font-semibold ${
                      p.enabled ? 'bg-brand-600 text-white' : 'bg-surface-tertiary text-txt-secondary'
                    }`}
                  >
                    {p.enabled ? 'Enabled' : 'Disabled'}
                  </button>
                </div>
              ))}
            </div>
          </Card>
          </>)}

          {tab === 'webhooks' && (
          <Card title="Webhooks" subtitle="All require an X-WeQuote-Secret header">
            <div className="space-y-2">
              {WEBHOOK_ENDPOINTS.map((ep) => (
                <div key={ep} className="flex items-center gap-2">
                  <span className="bg-brand-100 text-brand-700 px-1.5 py-0.5 rounded text-[10px] font-bold">
                    POST
                  </span>
                  <code className="font-mono text-xs text-txt-secondary">{ep}</code>
                </div>
              ))}
            </div>
          </Card>
          )}
        </div>
      </div>
    </div>
  )
}
